import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import {
  Plus, Compass, Download, Copy, Trash2, ArrowLeft, Send, Eye, HelpCircle, X, Check, Lock, Globe, Heart, User
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

import { CreatorLinks } from './components/CreatorLinks'
import type {
  MapRecord, Signals, VisualState, WisdomTile, Motif, CareBurden, TileSuggestion, LoadNote, HandoffState
} from './lib/types'
import {
  getOwnerKey, hashOwner, createMap, updateMap, getMap, listMyMaps, deleteMap,
  updateSignals, setAnchor, setPrivateNotes, setPrivateLegend, setVisualState,
  deriveVisualFromSignals, localAI, listPublicTilesLocal, getTile, saveLocalTile, resonateLocal, saveCurrentDraft, getCurrentDraft, clearCurrentDraft, computeSignature
} from './lib/localStore'
import {
  CARE_LABELS, MOTIF_LABEL, MOTIFS, computeSignatureString, canCarveTile, levelLabel, shortId, formatDate
} from './lib/utils'

// =====================================================
// API layer (real worker when present, full local parity otherwise)
// =====================================================
const API_BASE = '/api'

async function api<T>(path: string, init?: RequestInit & { ownerKey?: string }): Promise<T> {
  const key = init?.ownerKey || getOwnerKey()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Owner-Key': key,
    ...(init?.headers as Record<string, string> || {}),
  }
  try {
    const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
    if (!res.ok) throw new Error(`API ${res.status}`)
    return await res.json()
  } catch (e) {
    return localFallback(path, init, key) as any
  }
}

async function localFallback(path: string, init: any, ownerKey: string): Promise<any> {
  const h = hashOwner(ownerKey)

  if (path === '/map' && init?.method === 'POST') {
    const body = JSON.parse(init.body || '{}')
    const m = createMap(body.anchor || 'A thread I have been carrying')
    return { id: m.id, ownerKey }
  }

  if (path.startsWith('/map/') && !path.includes('/legend') && !path.includes('/tile')) {
    const id = path.split('/')[2]
    if (!init || init.method === 'GET') {
      const m = getMap(id, ownerKey)
      return m ? { map: m } : { error: 'not found' }
    }
    if (init?.method === 'PATCH') {
      const patch = JSON.parse(init.body || '{}')
      if (patch.anchor !== undefined) setAnchor(id, patch.anchor)
      if (patch.privateNotes !== undefined) setPrivateNotes(id, patch.privateNotes)
      if (patch.privateLegend !== undefined) setPrivateLegend(id, patch.privateLegend)
      if (patch.visualState) setVisualState(id, patch.visualState)
      const fresh = getMap(id, ownerKey)!
      return { map: fresh }
    }
    if (init?.method === 'DELETE') {
      deleteMap(id)
      return { ok: true }
    }
  }

  if (path.includes('/signals') && init?.method === 'PATCH') {
    const id = path.split('/')[2]
    const { signals, visualState } = JSON.parse(init.body || '{}')
    const m = updateSignals(id, signals, visualState)
    return { map: m }
  }

  if (path.includes('/legend') && init?.method === 'POST') {
    const body = JSON.parse(init.body || '{}')
    const legend = localAI.generateLegend(body.signals, body.anchor, body.privateNotes)
    const m = getMap(body.mapId, ownerKey)
    if (m) setPrivateLegend(body.mapId, legend)
    return { legend }
  }

  if (path.includes('/tile/suggest') && init?.method === 'POST') {
    const body = JSON.parse(init.body || '{}')
    const sugg = localAI.suggestTiles(body.signals, body.privateLegend)
    return { suggestions: sugg }
  }

  if (path.includes('/tile') && init?.method === 'POST') {
    const body = JSON.parse(init.body || '{}')
    const m = getMap(body.mapId, ownerKey)
    if (!m) return { error: 'map not found' }
    const id = 'tile_' + crypto.randomUUID().slice(0, 10)
    const tile: WisdomTile = {
      id,
      mapId: body.mapId,
      createdAt: new Date().toISOString(),
      publishedAt: body.isPublic ? new Date().toISOString() : undefined,
      isPublic: !!body.isPublic,
      tileText: body.tileText,
      motif: body.motif,
      signature: computeSignature(m.signals),
      resonanceCount: 0,
      contributedSignals: !!body.contributeSignals
    }
    saveLocalTile(tile)
    return { tile }
  }

  if (path === '/gallery') {
    const tiles = listPublicTilesLocal()
    return { tiles, total: tiles.length }
  }

  if (path.startsWith('/tile/') && path.includes('/resonate')) {
    const id = path.split('/')[2]
    const fp = 'local_' + Date.now().toString(36)
    const count = resonateLocal(id, fp)
    return { resonanceCount: count }
  }

  if (path === '/my/maps') {
    const maps = listMyMaps(ownerKey)
    return {
      maps: maps.map(m => ({
        id: m.id,
        createdAt: m.createdAt,
        updatedAt: m.updatedAt,
        anchor: m.anchor,
        signature: computeSignature(m.signals),
        hasTile: false, // local demo keeps tiles separate
        tileIsPublic: false
      }))
    }
  }

  return { ok: true }
}

// =====================================================
// The Moral Cartographer — main application
// =====================================================

type View = 'landing' | 'cartography' | 'atlas' | 'my' | 'tile-detail'

const DIMENSIONS = ['care', 'anticipation', 'memory', 'handoff', 'capacity'] as const
type Dim = typeof DIMENSIONS[number]

const DIM_LABELS: Record<Dim, string> = {
  care: 'Care burden',
  anticipation: 'Anticipation load',
  memory: 'Memory load',
  handoff: 'Handoff friction',
  capacity: 'Capacity signal'
}

const DIM_HELP: Record<Dim, string> = {
  care: 'What kind of relational work is this reply carrying or withholding?',
  anticipation: 'How much mental rehearsal, dread, or future scripting is already happening?',
  memory: 'How much prior context, shared history, and emotional residue is stacked here?',
  handoff: 'What makes cleanly passing the turn back difficult or risky?',
  capacity: 'What is your current internal resource for this conversation?'
}

export default function App() {
  const [view, setView] = useState<View>('landing')
  const [currentMap, setCurrentMap] = useState<MapRecord | null>(null)
  const [focusedDim, setFocusedDim] = useState<Dim | null>(null)
  const [ownerKey, setOwnerKey] = useState(getOwnerKey())
  const [galleryTiles, setGalleryTiles] = useState<WisdomTile[]>([])
  const [myMaps, setMyMaps] = useState<any[]>([])
  const [selectedTile, setSelectedTile] = useState<WisdomTile | null>(null)
  const [isGeneratingLegend, setIsGeneratingLegend] = useState(false)
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [tileSuggestions, setTileSuggestions] = useState<TileSuggestion[]>([])
  const [tileDraftText, setTileDraftText] = useState('')
  const [tileDraftMotif, setTileDraftMotif] = useState<Motif>('fog')
  const [showTileForger, setShowTileForger] = useState(false)
  const [publishConsent, setPublishConsent] = useState({ isPublic: true, contributeSignals: false, includeSnapshot: false, includeTileImage: false })
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showKeyModal, setShowKeyModal] = useState(false)

  // Live pentagon drag state (we keep signals + visual in sync)
  const [localVisual, setLocalVisual] = useState<VisualState | null>(null)

  const ownerHash = useMemo(() => hashOwner(ownerKey), [ownerKey])

  // Load draft or start fresh
  useEffect(() => {
    const draft = getCurrentDraft()
    if (draft && draft.id) {
      // Try to restore a previous in-progress map
      const existing = getMap(draft.id as string)
      if (existing) {
        setCurrentMap(existing)
        setLocalVisual(existing.visualState)
        setView('cartography')
      }
    }
  }, [])

  // Persist current map as draft when it changes
  useEffect(() => {
    if (currentMap) {
      saveCurrentDraft(currentMap)
    }
  }, [currentMap])

  // Keyboard niceties for overthinkers
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === '?' && view !== 'cartography') {
        e.preventDefault()
        setView('atlas')
      }
      if (e.key.toLowerCase() === 'n' && (view === 'landing' || view === 'atlas')) {
        e.preventDefault()
        startNewMap()
      }
      if (e.key === 'Escape') {
        if (showTileForger) setShowTileForger(false)
        else if (focusedDim) setFocusedDim(null)
        else if (view === 'cartography') closeMap()
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's' && view === 'cartography' && currentMap) {
        e.preventDefault()
        toast.success('Draft saved locally (and will sync when worker is present)')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [view, currentMap, focusedDim, showTileForger])

  // =====================================================
  // Cartography actions
  // =====================================================

  async function startNewMap(customAnchor?: string) {
    const anchor = customAnchor || prompt('What thread or person are you carrying? (private — e.g. "S. after the long silence" or "the project update I owe")') || 'A reply I have not sent'
    const res = await api<{ id: string; ownerKey: string }>('/map', { method: 'POST', body: JSON.stringify({ anchor }) })
    const fresh = await api<{ map: MapRecord }>(`/map/${res.id}`)
    setCurrentMap(fresh.map)
    setLocalVisual(fresh.map.visualState)
    setFocusedDim(null)
    setShowTileForger(false)
    setTileSuggestions([])
    setView('cartography')
    toast('New map started. Nothing is saved until you choose.', { description: 'Close the tab anytime. Your draft lives locally.' })
  }

  function closeMap(saveDraft = true) {
    if (!saveDraft) clearCurrentDraft()
    setCurrentMap(null)
    setLocalVisual(null)
    setFocusedDim(null)
    setShowTileForger(false)
    setView('landing')
  }

  function updateLocalSignals(next: Signals) {
    if (!currentMap) return
    const nextVisual = deriveVisualFromSignals(next, localVisual || currentMap.visualState)
    setLocalVisual(nextVisual)

    // Optimistic local update + try to persist
    const updated = { ...currentMap, signals: next, visualState: nextVisual }
    setCurrentMap(updated)

    api(`/map/${currentMap.id}/signals`, {
      method: 'PATCH',
      body: JSON.stringify({ signals: next, visualState: nextVisual })
    }).then((r: any) => {
      if (r.map) setCurrentMap(r.map)
    })
  }

  function updateVisualDirectly(vs: VisualState) {
    if (!currentMap) return
    setLocalVisual(vs)
    const updated = { ...currentMap, visualState: vs }
    setCurrentMap(updated)

    // Reverse-derive approximate signal levels from visual (so the notebook stays in sync)
    const nextSignals = reverseSignalsFromVisual(vs, currentMap.signals)
    api(`/map/${currentMap.id}/signals`, {
      method: 'PATCH',
      body: JSON.stringify({ signals: nextSignals, visualState: vs })
    })
  }

  function reverseSignalsFromVisual(vs: VisualState, prev: Signals): Signals {
    return {
      care_burdens: prev.care_burdens.length ? prev.care_burdens : (vs.care > 0.55 ? ['repair'] : []),
      anticipation: { level: Math.max(1, Math.min(3, Math.round(vs.anticipation * 3))) as 1 | 2 | 3, note: prev.anticipation.note },
      memory: { level: Math.max(1, Math.min(3, Math.round(vs.memory * 3))) as 1 | 2 | 3, note: prev.memory.note },
      handoff: { level: Math.max(1, Math.min(3, Math.round(vs.handoff * 3))) as 1 | 2 | 3, frictions: prev.handoff.frictions, note: prev.handoff.note },
      capacity: { level: Math.max(1, Math.min(3, Math.round(vs.capacity * 3))) as 1 | 2 | 3, note: prev.capacity.note }
    }
  }

  async function requestLegend() {
    if (!currentMap) return
    setIsGeneratingLegend(true)
    try {
      const res = await api<{ legend: string }>(`/map/${currentMap.id}/legend`, {
        method: 'POST',
        body: JSON.stringify({
          mapId: currentMap.id,
          signals: currentMap.signals,
          anchor: currentMap.anchor,
          privateNotes: currentMap.privateNotes
        })
      })
      setPrivateLegend(currentMap.id, res.legend)
      setCurrentMap({ ...currentMap, privateLegend: res.legend })
      toast.success('Legend written', { description: 'Private to you. Edit it anytime.' })
    } finally {
      setIsGeneratingLegend(false)
    }
  }

  async function openTileForger() {
    if (!currentMap || !canCarveTile(currentMap)) {
      toast.error('Map needs a little more shape', { description: 'Add at least one care burden and touch all five dimensions.' })
      return
    }
    setIsSuggesting(true)
    setShowTileForger(true)
    setTileDraftText('')
    try {
      const res = await api<{ suggestions: TileSuggestion[] }>(`/map/${currentMap.id}/tile/suggest`, {
        method: 'POST',
        body: JSON.stringify({
          mapId: currentMap.id,
          signals: currentMap.signals,
          privateLegend: currentMap.privateLegend,
          anchorHint: currentMap.anchor.split(' ').slice(0, 2).join(' ')
        })
      })
      setTileSuggestions(res.suggestions || localAI.suggestTiles(currentMap.signals, currentMap.privateLegend))
      if (res.suggestions?.[0]) {
        setTileDraftText(res.suggestions[0].text)
        setTileDraftMotif(res.suggestions[0].motif)
      } else {
        const local = localAI.suggestTiles(currentMap.signals)
        setTileDraftText(local[0]?.text || '')
        setTileDraftMotif(local[0]?.motif || 'fog')
      }
    } finally {
      setIsSuggesting(false)
    }
  }

  function applySuggestion(s: TileSuggestion) {
    setTileDraftText(s.text)
    setTileDraftMotif(s.motif)
  }

  async function publishTile(isPublic: boolean) {
    if (!currentMap || !tileDraftText.trim()) return

    const consent = { ...publishConsent, isPublic }

    // Strong ethical preview already happened in the forger UI.
    // One last explicit gate for public.
    if (isPublic) {
      const ok = window.confirm(
        'This wisdom tile will be visible to anyone who visits The Atlas.\n\n' +
        'It contains only the text you approved and the motif you chose.\n' +
        'Your private notes, the full anchor, and exact map coordinates stay private.\n\n' +
        'Publish to the public Atlas?'
      )
      if (!ok) return
    }

    const res = await api<{ tile: WisdomTile }>(`/map/${currentMap.id}/tile`, {
      method: 'POST',
      body: JSON.stringify({
        mapId: currentMap.id,
        tileText: tileDraftText.trim(),
        motif: tileDraftMotif,
        isPublic,
        contributeSignals: consent.contributeSignals,
        includeMapSnapshot: consent.includeSnapshot,
        includeTileImage: consent.includeTileImage
      })
    })

    const tile = res.tile
    saveLocalTile(tile)

    // Opt-in R2 artifact upload (client renders beautiful card → base64 PNG → /api/artifact)
    if (consent.includeTileImage) {
      try {
        const dataUrl = await renderTileCardToDataURL(tileDraftText.trim(), tileDraftMotif)
        const b64 = dataUrl.split(',')[1]
        await api('/artifact', {
          method: 'POST',
          body: JSON.stringify({
            tileId: tile.id,
            kind: 'tile',
            data: b64,
            consentNote: 'user explicitly opted in during publish'
          })
        })
      } catch (e) {
        // non-fatal — the tile text is already saved
        console.log('tile image upload skipped', e)
      }
    }
    if (consent.includeSnapshot && currentMap) {
      try {
        const dataUrl = await renderMapSnapshotToDataURL(currentMap, localVisual || currentMap.visualState)
        const b64 = dataUrl.split(',')[1]
        await api('/artifact', {
          method: 'POST',
          body: JSON.stringify({
            mapId: currentMap.id,
            kind: 'snapshot',
            data: b64,
            consentNote: 'user explicitly opted in during publish'
          })
        })
      } catch (e) {
        console.log('map snapshot upload skipped', e)
      }
    }

    setShowTileForger(false)
    setPublishConsent({ isPublic: true, contributeSignals: false, includeSnapshot: false, includeTileImage: false })

    toast.success(isPublic ? 'Tile published to The Atlas' : 'Private wisdom tile saved', {
      description: isPublic ? 'Anyone can find it. You can unpublish later from My Cartography.' : 'Only you can see it.'
    })

    // Refresh gallery if public
    if (isPublic) loadGallery()

    // Offer to view it
    setSelectedTile(tile)
    setView('tile-detail')
  }

  // Client-side renderers for opt-in R2 artifacts (pure, no external deps)
  async function renderTileCardToDataURL(text: string, motif: Motif): Promise<string> {
    const canvas = document.createElement('canvas')
    canvas.width = 720
    canvas.height = 520
    const ctx = canvas.getContext('2d', { alpha: true })!
    // Paper background
    ctx.fillStyle = '#f4f1e9'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#d4c9b5'
    ctx.lineWidth = 2
    ctx.strokeRect(24, 24, canvas.width - 48, canvas.height - 48)

    // Motif mark (simple elegant symbols)
    ctx.fillStyle = '#5c5549'
    ctx.font = '600 13px system-ui'
    ctx.fillText(MOTIF_LABEL[motif].toUpperCase(), 48, 72)

    // The wisdom text
    ctx.fillStyle = '#111'
    ctx.font = '400 21px Georgia, serif'
    const words = text.split(' ')
    let line = ''
    let y = 140
    for (const w of words) {
      const test = line + w + ' '
      if (ctx.measureText(test).width > 620 && line) {
        ctx.fillText(line.trim(), 48, y)
        line = w + ' '
        y += 34
      } else {
        line = test
      }
    }
    if (line) ctx.fillText(line.trim(), 48, y)

    // Footer
    ctx.fillStyle = '#5c5549'
    ctx.font = '400 11px system-ui'
    ctx.fillText('ANONYMOUS — FROM THE ATLAS OF UNSENT REPLIES', 48, canvas.height - 52)

    return canvas.toDataURL('image/png')
  }

  async function renderMapSnapshotToDataURL(map: MapRecord, vs: VisualState): Promise<string> {
    const canvas = document.createElement('canvas')
    canvas.width = 720
    canvas.height = 420
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#1c1a16'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const cx = 360, cy = 210, r = 150
    const angles = [-90, -18, 54, 126, 198]
    const radii = [vs.care, vs.anticipation, vs.memory, vs.handoff, vs.capacity]
    const pts = radii.map((rad, i) => {
      const a = (angles[i] * Math.PI) / 180
      return { x: cx + Math.cos(a) * r * rad, y: cy + Math.sin(a) * r * rad }
    })

    // terrain fill
    ctx.beginPath()
    pts.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y) })
    ctx.closePath()
    ctx.fillStyle = 'rgba(138,127,104,0.18)'
    ctx.fill()
    ctx.strokeStyle = '#8a7f68'
    ctx.lineWidth = 2
    ctx.stroke()

    // spokes + labels
    ctx.strokeStyle = '#3a3630'
    ctx.lineWidth = 1
    pts.forEach((p, i) => {
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
    })

    ctx.fillStyle = '#f4f1e9'
    ctx.font = '11px system-ui'
    const labels = ['CARE', 'ANTICIPATION', 'MEMORY', 'HANDOFF', 'CAPACITY']
    pts.forEach((p, i) => {
      ctx.fillText(labels[i], p.x - 30, p.y - 12)
    })

    ctx.fillStyle = '#8a8173'
    ctx.font = '10px system-ui'
    ctx.fillText('PRIVATE TERRAIN SNAPSHOT — NOT FOR PUBLICATION', 40, canvas.height - 28)

    return canvas.toDataURL('image/png')
  }

  async function loadGallery() {
    const res = await api<{ tiles: WisdomTile[] }>('/gallery')
    setGalleryTiles(res.tiles || listPublicTilesLocal())
  }

  async function loadMyMaps() {
    const res = await api<{ maps: any[] }>('/my/maps')
    setMyMaps(res.maps || listMyMaps())
  }

  async function openMap(id: string) {
    const res = await api<{ map: MapRecord }>(`/map/${id}`)
    if (res.map) {
      setCurrentMap(res.map)
      setLocalVisual(res.map.visualState)
      setView('cartography')
      setShowTileForger(false)
    }
  }

  async function resonate(tileId: string) {
    const res = await api<{ resonanceCount: number }>(`/tile/${tileId}/resonate`, { method: 'POST' })
    setGalleryTiles(prev => prev.map(t => t.id === tileId ? { ...t, resonanceCount: res.resonanceCount } : t))
    if (selectedTile && selectedTile.id === tileId) {
      setSelectedTile({ ...selectedTile, resonanceCount: res.resonanceCount })
    }
    toast('Thank you for marking resonance', { description: 'Someone else will feel less alone because you did this.' })
  }

  function traceTerrain(tile: WisdomTile) {
    // Seed a new private map with the signature shape
    const parts = tile.signature.split(',')
    const care = parts[0] as CareBurden
    const antLevel = parts[1].includes('high') ? 3 : parts[1].includes('med') ? 2 : 1
    const memLevel = parts[2].includes('high') ? 3 : parts[2].includes('med') ? 2 : 1
    const handLevel = parts[3].includes('knot') ? 3 : parts[3].includes('sticky') ? 2 : 1
    const capLevel = parts[4].includes('thin') ? 1 : parts[4].includes('modest') ? 2 : 3

    const seededSignals: Signals = {
      care_burdens: [care].filter(Boolean) as CareBurden[],
      anticipation: { level: antLevel as 1 | 2 | 3 },
      memory: { level: memLevel as 1 | 2 | 3 },
      handoff: { level: handLevel as 1 | 2 | 3, frictions: [] },
      capacity: { level: capLevel as 1 | 2 | 3 }
    }
    const vs = deriveVisualFromSignals(seededSignals)

    // Create fresh map then patch signals
    const anchor = 'A similar weight I am carrying'
    api('/map', { method: 'POST', body: JSON.stringify({ anchor }) }).then(async (r: any) => {
      const id = r.id
      await api(`/map/${id}/signals`, { method: 'PATCH', body: JSON.stringify({ signals: seededSignals, visualState: vs }) })
      const fresh = await api<{ map: MapRecord }>(`/map/${id}`)
      setCurrentMap(fresh.map)
      setLocalVisual(fresh.map.visualState)
      setView('cartography')
      setSelectedTile(null)
      toast('Terrain traced', { description: 'This is now your private map. The original tile stays unchanged.' })
    })
  }

  async function deleteCurrentMap() {
    if (!currentMap) return
    if (!confirm('Delete this map permanently? This cannot be undone.')) return
    await api(`/map/${currentMap.id}`, { method: 'DELETE' })
    clearCurrentDraft()
    closeMap(false)
    toast('Map deleted')
  }

  // =====================================================
  // Pentago n — the heart of the moral cartographer
  // =====================================================

  const PENTAGON_CX = 420
  const PENTAGON_CY = 260
  const PENTAGON_R = 195

  function getPentagonPoints(vs: VisualState) {
    const angles = [-90, -18, 54, 126, 198] // top then clockwise
    const radii = [vs.care, vs.anticipation, vs.memory, vs.handoff, vs.capacity]
    return radii.map((r, i) => {
      const rad = ((angles[i]) * Math.PI) / 180
      return {
        x: PENTAGON_CX + Math.cos(rad) * PENTAGON_R * r,
        y: PENTAGON_CY + Math.sin(rad) * PENTAGON_R * r,
        dim: DIMENSIONS[i]
      }
    })
  }

  const pentagonPoints = useMemo(() => getPentagonPoints(localVisual || currentMap?.visualState || { care: 0.5, anticipation: 0.5, memory: 0.5, handoff: 0.5, capacity: 0.5 }), [localVisual, currentMap])

  function onVertexPointerDown(e: React.PointerEvent, dim: Dim, idx: number) {
    e.preventDefault()
    ;(e.target as SVGElement).setPointerCapture(e.pointerId)
    setFocusedDim(dim)

    const startX = e.clientX
    const startY = e.clientY
    const base = localVisual || currentMap!.visualState
    const startRadius = (base as any)[dim] as number

    function onMove(ev: PointerEvent) {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      // Project movement along the spoke direction for that vertex
      const angle = ([-90, -18, 54, 126, 198][idx] * Math.PI) / 180
      const proj = (dx * Math.cos(angle) + dy * Math.sin(angle)) / PENTAGON_R
      let nextR = Math.max(0.12, Math.min(1.0, startRadius + proj * 1.35))
      const nextVS: VisualState = { ...(localVisual || base), [dim]: nextR }
      updateVisualDirectly(nextVS)
    }
    function onUp() {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // =====================================================
  // Rich per-dimension editors (the "surveyor's notebook")
  // =====================================================

  function renderDimensionEditor(dim: Dim) {
    if (!currentMap) return null
    const s = currentMap.signals

    if (dim === 'care') {
      const selected = new Set(s.care_burdens)
      const toggle = (b: CareBurden) => {
        const next = new Set(selected)
        if (next.has(b)) next.delete(b)
        else next.add(b)
        const nextSignals: Signals = { ...s, care_burdens: Array.from(next) as CareBurden[] }
        updateLocalSignals(nextSignals)
      }
      return (
        <div className="space-y-3">
          <div className="text-[10px] tracking-[2px] text-[var(--text-subtle)]">WHAT KIND OF CARE IS AT STAKE?</div>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(CARE_LABELS) as CareBurden[]).map(b => (
              <button
                key={b}
                onClick={() => toggle(b)}
                className={`text-left px-3 py-2 rounded border text-sm transition ${selected.has(b) ? 'border-[var(--accent)] bg-[#1f1c17]' : 'border-[var(--border)] hover:border-[var(--text-subtle)]'}`}
              >
                {CARE_LABELS[b]}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-[var(--text-subtle)]">You can choose more than one. This is the terrain of the debt.</div>
        </div>
      )
    }

    if (dim === 'handoff') {
      const hs = s.handoff
      const toggleF = (f: string) => {
        const nextFr = hs.frictions.includes(f) ? hs.frictions.filter(x => x !== f) : [...hs.frictions, f]
        const next: Signals = { ...s, handoff: { ...hs, frictions: nextFr } }
        updateLocalSignals(next)
      }
      return (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            {[1, 2, 3].map(l => (
              <button key={l} onClick={() => updateLocalSignals({ ...s, handoff: { ...hs, level: l as 1 | 2 | 3 } })}
                className={`px-3 py-1 text-xs rounded border ${hs.level === l ? 'border-[var(--accent)]' : 'border-[var(--border)]'}`}>
                {l === 1 ? 'open' : l === 2 ? 'sticky' : 'knotted'}
              </button>
            ))}
          </div>
          <div className="text-[10px] tracking-[2px] text-[var(--text-subtle)] mt-1">WHAT MAKES THE PASS DIFFICULT?</div>
          <div className="flex flex-wrap gap-1.5">
            {['no clean close', 'tone risk (will be misread)', 'asymmetry of investment', 'they may need more than I have', 'the silence is already speaking', 'fear the reply chain will explode', 'I do not know what I feel yet'].map(f => (
              <button key={f} onClick={() => toggleF(f)}
                className={`text-xs px-2.5 py-1 rounded border ${hs.frictions.includes(f) ? 'border-[var(--accent)] bg-[#1f1c17]' : 'border-[var(--border)]'}`}>
                {f}
              </button>
            ))}
          </div>
          <textarea
            className="w-full bg-transparent border border-[var(--border)] rounded p-2 text-sm focus:outline-none focus:border-[var(--accent)]"
            placeholder="Other friction (optional)…"
            value={hs.note || ''}
            onChange={e => updateLocalSignals({ ...s, handoff: { ...hs, note: e.target.value } })}
          />
        </div>
      )
    }

    // anticipation, memory, capacity — simple level + note
    const load = (s as any)[dim] as LoadNote
    const isCap = dim === 'capacity'
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          {[1, 2, 3].map(l => (
            <button key={l} onClick={() => {
              const next: Signals = { ...s, [dim]: { ...load, level: l as 1 | 2 | 3 } } as any
              updateLocalSignals(next)
            }}
              className={`flex-1 py-2 text-sm rounded border ${load.level === l ? 'border-[var(--accent)] bg-[#1f1c17]' : 'border-[var(--border)] hover:border-[var(--text-subtle)]'}`}>
              {levelLabel(l as 1 | 2 | 3, isCap ? 'capacity' : 'load')}
            </button>
          ))}
        </div>
        <textarea
          className="w-full bg-transparent border border-[var(--border)] rounded p-2 text-sm h-20 focus:outline-none focus:border-[var(--accent)]"
          placeholder={dim === 'anticipation' ? 'What future conversation am I already having?' : dim === 'memory' ? 'Which previous threads or versions of us are present right now?' : 'What is true about my reserves today?'}
          value={load.note || ''}
          onChange={e => {
            const next: Signals = { ...s, [dim]: { ...load, note: e.target.value } } as any
            updateLocalSignals(next)
          }}
        />
      </div>
    )
  }

  // =====================================================
  // Render
  // =====================================================

  const vs = localVisual || currentMap?.visualState
  const sigString = currentMap ? computeSignatureString(currentMap.signals) : ''

  return (
    <div className="min-h-screen bg-[#0f0e0c] text-[#e8e4d9] selection:bg-[#8a7f68] selection:text-[#0f0e0c]">
      {/* Calm top bar */}
      <div className="border-b border-[var(--border)] bg-[#0f0e0c]/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            <button onClick={() => { if (view === 'cartography' && currentMap) closeMap(); else setView('landing') }} className="flex items-center gap-2 hover:text-[var(--text-bright)]">
              <Compass className="w-4 h-4" /> cartographer
            </button>
            <div className="text-[10px] px-2 py-px rounded bg-[#1c1a16] text-[var(--text-subtle)] tracking-[1.5px]">MORAL CARTOGRAPHY OF THE UNSENT REPLY</div>
          </div>

          <div className="flex items-center gap-4 text-xs">
            <button onClick={() => { loadGallery(); setView('atlas') }} className="hover:text-[var(--text-bright)] flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> THE ATLAS</button>
            <button onClick={() => { loadMyMaps(); setView('my') }} className="hover:text-[var(--text-bright)] flex items-center gap-1"><User className="w-3.5 h-3.5" /> MY CARTOGRAPHY</button>
            <button onClick={() => setShowPrivacy(true)} className="hover:text-[var(--text-bright)] flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> PRIVACY</button>
            <button onClick={() => setShowKeyModal(true)} className="text-[var(--text-subtle)] hover:text-[var(--text-bright)]">YOUR KEY</button>
            <CreatorLinks variant="compact" />
          </div>
        </div>
      </div>

      {/* LANDING */}
      {view === 'landing' && (
        <div className="max-w-3xl mx-auto px-5 pt-16 pb-24">
          <div className="max-w-[46ch]">
            <div className="uppercase tracking-[3px] text-xs text-[var(--text-subtle)] mb-3">A FREE INSTRUMENT ON THE EDGE</div>
            <h1 className="text-5xl leading-none font-light tracking-tight map-serif">The weight of the reply<br />you have not sent yet.</h1>
            <p className="mt-6 text-lg text-[var(--text-bright)]">A moral cartographer for people who overthink social silences. Map the terrain of a delayed reply without collapsing into self-blame or diagnosis.</p>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => startNewMap()} className="px-8 py-3 rounded bg-[#f4f1e9] text-[#111] text-sm tracking-widest hover:bg-white flex items-center gap-2">BEGIN A NEW MAP <Plus size={16} /></button>
            <button onClick={() => { loadGallery(); setView('atlas') }} className="px-8 py-3 rounded border border-[var(--border)] text-sm tracking-widest hover:border-[var(--text-subtle)]">ENTER THE ATLAS</button>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {[
              ['Care burden type', 'What kind of relational work is stalled or being withheld.'],
              ['Anticipation, memory, handoff, capacity', 'The four other loads that usually travel with a delayed reply.'],
              ['Public wisdom tiles', 'From your private map you may carve a small, de-identified gift for others carrying similar weight.']
            ].map(([title, desc], i) => (
              <div key={i} className="card p-4">
                <div className="text-[var(--accent)] text-xs tracking-[2px] mb-1.5">{title}</div>
                <div className="text-[var(--text-bright)]">{desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-xs text-[var(--text-subtle)] max-w-prose">
            Nothing is published without your explicit, repeated consent. The raw map (your notes, exact coordinates, full anchor) stays private. The only thing that can become public is a short wisdom tile you approved after seeing a live preview.
          </div>

          <CreatorLinks variant="full" />
        </div>
      )}

      {/* THE CARTOGRAPHY SURFACE */}
      {view === 'cartography' && currentMap && vs && (
        <div className="max-w-5xl mx-auto px-5 pt-8 pb-16">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="uppercase text-[10px] tracking-[2.5px] text-[var(--text-subtle)]">YOUR PRIVATE TERRAIN</div>
              <input
                value={currentMap.anchor}
                onChange={e => {
                  const val = e.target.value
                  setCurrentMap({ ...currentMap, anchor: val })
                  setAnchor(currentMap.id, val)
                }}
                className="mt-1 text-2xl map-serif bg-transparent border-b border-transparent focus:border-[var(--border)] focus:outline-none w-[28ch]"
                placeholder="Who or what is waiting?"
              />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button onClick={deleteCurrentMap} className="flex items-center gap-1 px-3 py-1.5 rounded border border-[var(--border)] hover:border-red-900/60 text-[var(--text-subtle)]"><Trash2 size={14} /> DELETE</button>
              <button onClick={() => closeMap()} className="px-4 py-1.5 rounded border border-[var(--border)]">CLOSE MAP</button>
              <button onClick={openTileForger} disabled={!canCarveTile(currentMap)} className="px-5 py-1.5 rounded bg-[#f4f1e9] text-[#111] disabled:opacity-40 flex items-center gap-2">CARVE WISDOM TILE <Send size={15} /></button>
            </div>
          </div>

          <div className="text-xs text-[var(--text-subtle)] mb-2">{sigString}</div>

          {/* THE MAP — Moral Pentagon */}
          <div className="card p-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* SVG Surface */}
              <div className="relative">
                <svg width="840" height="520" className="map-surface rounded-xl" viewBox="0 0 840 520">
                  {/* Soft paper texture simulation */}
                  <defs>
                    <pattern id="paper" patternUnits="userSpaceOnUse" width="140" height="140">
                      <rect width="140" height="140" fill="#1c1a16" />
                      <circle cx="20" cy="30" r="0.6" fill="#2a2721" />
                      <circle cx="90" cy="80" r="0.4" fill="#2a2721" />
                    </pattern>
                  </defs>
                  <rect x="60" y="30" width="720" height="460" rx="8" fill="url(#paper)" />

                  {/* Concentric reference circles (cartographic) */}
                  {[0.3, 0.55, 0.8].map((r, i) => (
                    <polygon key={i} points={getPentagonPoints({ care: r, anticipation: r, memory: r, handoff: r, capacity: r }).map(p => `${p.x},${p.y}`).join(' ')}
                      fill="none" stroke="#3a3630" strokeWidth="1" />
                  ))}

                  {/* The live terrain polygon */}
                  <polygon
                    points={pentagonPoints.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="rgba(138,127,104,0.12)"
                    stroke="#8a7f68"
                    strokeWidth="1.5"
                  />

                  {/* Spokes */}
                  {pentagonPoints.map((p, i) => (
                    <line key={i} x1={PENTAGON_CX} y1={PENTAGON_CY} x2={p.x} y2={p.y} stroke="#4a463f" strokeWidth="1" />
                  ))}

                  {/* Vertices — draggable */}
                  {pentagonPoints.map((p, i) => {
                    const dim = p.dim as Dim
                    const isFocused = focusedDim === dim
                    return (
                      <g key={i}>
                        <circle
                          cx={p.x} cy={p.y} r={isFocused ? 13 : 9}
                          fill="#f4f1e9"
                          stroke="#8a7f68"
                          strokeWidth="2.5"
                          className="cursor-grab active:cursor-grabbing"
                          onPointerDown={(e) => onVertexPointerDown(e, dim, i)}
                        />
                        <text x={p.x} y={p.y - 22} textAnchor="middle" fontSize="10" fill="#8a8173" className="select-none pointer-events-none">{DIM_LABELS[dim].split(' ')[0]}</text>
                      </g>
                    )
                  })}

                  {/* Center label */}
                  <text x={PENTAGON_CX} y={PENTAGON_CY + 5} textAnchor="middle" fontSize="11" fill="#5f5a52" className="select-none pointer-events-none tracking-[2px]">YOUR TERRAIN</text>
                </svg>

                <div className="absolute bottom-4 left-4 text-[10px] text-[var(--text-subtle)] bg-[#0f0e0c]/80 px-2 py-px rounded">Drag the vertices. The shape is the map.</div>
              </div>

              {/* Notebook / dimensions */}
              <div className="flex-1 min-w-[320px]">
                <div className="uppercase text-[10px] tracking-[2px] text-[var(--text-subtle)] mb-3">SURVEYOR’S NOTEBOOK</div>

                <div className="space-y-2">
                  {DIMENSIONS.map(dim => {
                    const active = focusedDim === dim
                    const level = (currentMap.signals as any)[dim]?.level || (dim === 'care' ? currentMap.signals.care_burdens.length : 2)
                    return (
                      <button key={dim} onClick={() => setFocusedDim(active ? null : dim)} className={`w-full text-left border rounded-lg p-3 transition ${active ? 'border-[var(--accent)] bg-[#1a1814]' : 'border-[var(--border)] hover:border-[#5a554b]'}`}>
                        <div className="flex justify-between text-sm">
                          <div>{DIM_LABELS[dim]}</div>
                          <div className="text-[var(--text-subtle)]">{dim === 'care' ? currentMap.signals.care_burdens.length + ' chosen' : levelLabel(level, dim === 'capacity' ? 'capacity' : 'load')}</div>
                        </div>
                        <div className="text-xs text-[var(--text-subtle)] mt-0.5 pr-6">{DIM_HELP[dim]}</div>
                      </button>
                    )
                  })}
                </div>

                <AnimatePresence>
                  {focusedDim && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4 border border-[var(--border)] rounded-xl p-4 bg-[#161512]">
                      {renderDimensionEditor(focusedDim)}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Field notes */}
                <div className="mt-6">
                  <div className="uppercase text-[10px] tracking-[2px] text-[var(--text-subtle)] mb-2">FIELD NOTES (PRIVATE)</div>
                  <textarea
                    value={currentMap.privateNotes || ''}
                    onChange={e => {
                      const val = e.target.value
                      setCurrentMap({ ...currentMap, privateNotes: val })
                      setPrivateNotes(currentMap.id, val)
                    }}
                    className="w-full h-24 bg-transparent border border-[var(--border)] rounded-lg p-3 text-sm focus:outline-none focus:border-[var(--accent)]"
                    placeholder="Anything else that is true here that the five dimensions do not yet hold…"
                  />
                </div>

                {/* Legend */}
                <div className="mt-5">
                  <button onClick={requestLegend} disabled={isGeneratingLegend} className="text-xs flex items-center gap-2 text-[var(--accent)] hover:text-[var(--text-bright)] disabled:opacity-60">
                    <Compass size={14} /> {currentMap.privateLegend ? 'RE-READ THE TERRAIN' : 'ASK THE CARTOGRAPHER TO READ THIS TERRAIN'}
                  </button>
                  {currentMap.privateLegend && (
                    <div className="mt-2 text-sm leading-relaxed border-l-2 border-[var(--accent)] pl-3 text-[var(--text-bright)] whitespace-pre-wrap">{currentMap.privateLegend}</div>
                  )}
                  {currentMap.privateLegend && (
                    <button onClick={() => {
                      const edited = (prompt('Edit your private legend', currentMap.privateLegend) ?? currentMap.privateLegend) || ''
                      setPrivateLegend(currentMap.id, edited)
                      setCurrentMap({ ...currentMap, privateLegend: edited })
                    }} className="text-[10px] mt-1 text-[var(--text-subtle)] underline">edit</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 text-[10px] text-[var(--text-subtle)] flex items-center gap-4">
            <div>Press <span className="font-mono">Esc</span> to close. <span className="font-mono">Cmd/Ctrl+S</span> saves draft locally.</div>
            <div className="flex-1 h-px bg-[var(--border)]" />
            <CreatorLinks variant="compact" />
          </div>
        </div>
      )}

      {/* TILE FORGER — explicit, ethical, preview-first */}
      <AnimatePresence>
        {showTileForger && currentMap && (
          <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setShowTileForger(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={e => e.stopPropagation()} className="bg-[#161512] border border-[var(--border)] rounded-2xl w-full max-w-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
                <div>
                  <div className="text-sm tracking-[2px] text-[var(--accent)]">CARVE A WISDOM TILE</div>
                  <div className="text-xs text-[var(--text-subtle)]">Only what you approve here can ever become public. You will see an exact preview.</div>
                </div>
                <button onClick={() => setShowTileForger(false)}><X /></button>
              </div>

              <div className="p-6 grid md:grid-cols-5 gap-6">
                {/* Editor */}
                <div className="md:col-span-3 space-y-4">
                  <div>
                    <div className="text-xs mb-1.5 text-[var(--text-subtle)]">THE WISDOM (EDIT FREELY — THIS IS WHAT PEOPLE WILL READ)</div>
                    <textarea value={tileDraftText} onChange={e => setTileDraftText(e.target.value)} className="w-full h-40 bg-[#0f0e0c] border border-[var(--border)] rounded p-4 text-[15px] leading-snug focus:outline-none" />
                  </div>

                  <div>
                    <div className="text-xs mb-1.5 text-[var(--text-subtle)]">MOTIF</div>
                    <div className="flex flex-wrap gap-2">
                      {MOTIFS.map(m => (
                        <button key={m} onClick={() => setTileDraftMotif(m)} className={`px-3 py-1 rounded text-sm border ${tileDraftMotif === m ? 'border-[var(--accent)] bg-[#1f1c17]' : 'border-[var(--border)]'}`}>{MOTIF_LABEL[m]}</button>
                      ))}
                    </div>
                  </div>

                  {tileSuggestions.length > 0 && (
                    <div>
                      <div className="text-xs mb-1.5 text-[var(--text-subtle)]">SUGGESTIONS FROM THE MAP (CLICK TO USE)</div>
                      <div className="space-y-2">
                        {tileSuggestions.map((s, i) => (
                          <button key={i} onClick={() => applySuggestion(s)} className="text-left text-sm w-full p-3 rounded border border-[var(--border)] hover:border-[var(--accent)]">
                            “{s.text}” <span className="text-[var(--text-subtle)] text-xs">— {MOTIF_LABEL[s.motif]}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t border-[var(--border)] text-xs space-y-2 text-[var(--text-subtle)]">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={publishConsent.contributeSignals} onChange={e => setPublishConsent(p => ({ ...p, contributeSignals: e.target.checked }))} className="mt-1" />
                      <span>Also contribute my five terrain signals (care, anticipation, memory, handoff, capacity) as anonymous aggregate data. This helps surface patterns like “X% of high-anticipation maps also show thin capacity” without ever identifying anyone.</span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={publishConsent.includeSnapshot} onChange={e => setPublishConsent(p => ({ ...p, includeSnapshot: e.target.checked }))} className="mt-1" />
                      <span>Opt-in: upload a rendered snapshot of my private terrain drawing to R2 (only the visual shape + the five labels, no notes or anchor).</span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={publishConsent.includeTileImage} onChange={e => setPublishConsent(p => ({ ...p, includeTileImage: e.target.checked }))} className="mt-1" />
                      <span>Opt-in: upload a beautiful illustrated version of this exact tile card.</span>
                    </label>
                  </div>
                </div>

                {/* Live Preview — the exact public artifact */}
                <div className="md:col-span-2">
                  <div className="text-xs mb-2 text-[var(--text-subtle)]">EXACT PUBLIC PREVIEW — THIS IS WHAT WILL BE SEEN</div>
                  <div className="receipt p-5 rounded-xl text-[#111] text-[15px] leading-snug min-h-[210px] flex flex-col">
                    <div className="uppercase tracking-[2px] text-[10px] text-[#5c5549] mb-2">A WISDOM TILE FROM THE ATLAS</div>
                    <div className="map-serif flex-1">“{tileDraftText || 'Your wisdom will appear here…'}”</div>
                    <div className="text-[10px] text-[#5c5549] mt-4 flex items-center gap-2">
                      <span>{MOTIF_LABEL[tileDraftMotif]}</span>
                      <span className="flex-1 h-px bg-[#d4c9b5]" />
                      <span>ANONYMOUS</span>
                    </div>
                  </div>
                  <div className="text-[10px] mt-3 text-[var(--text-subtle)]">You can always edit the text again later from My Cartography before or after publishing.</div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-[var(--border)] flex flex-wrap gap-3 bg-[#11100e]">
                <button onClick={() => publishTile(false)} className="flex-1 py-2.5 rounded border border-[var(--border)] text-sm">KEEP AS PRIVATE ARTIFACT (ONLY I CAN SEE)</button>
                <button onClick={() => publishTile(true)} className="flex-1 py-2.5 rounded bg-[#f4f1e9] text-[#111] text-sm font-medium">PUBLISH TO THE PUBLIC ATLAS</button>
              </div>
              <div className="px-6 pb-4 text-[10px] text-[var(--text-subtle)]">Making uncertainty visible without embarrassing people is the entire point of this instrument. If the preview feels even slightly exposing, do not publish.</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* THE ATLAS */}
      {view === 'atlas' && (
        <div className="max-w-5xl mx-auto px-5 pt-10 pb-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs tracking-[3px] text-[var(--text-subtle)]">THE COLLECTIVE</div>
              <div className="text-3xl map-serif">The Atlas of Unsent Replies</div>
            </div>
            <button onClick={() => startNewMap()} className="text-sm flex items-center gap-2 px-4 py-2 border border-[var(--border)] rounded">START YOUR OWN MAP <Plus size={15} /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryTiles.length === 0 && <div className="text-sm text-[var(--text-subtle)]">Loading tiles…</div>}
            {galleryTiles.map(tile => (
              <div key={tile.id} onClick={() => { setSelectedTile(tile); setView('tile-detail') }} className="receipt p-5 rounded-2xl text-[#111] cursor-pointer hover:shadow-xl transition flex flex-col">
                <div className="uppercase text-[10px] text-[#5c5549] tracking-widest mb-2">{MOTIF_LABEL[tile.motif]}</div>
                <div className="map-serif flex-1 text-[15px] leading-snug">“{tile.tileText}”</div>
                <div className="mt-6 flex items-center justify-between text-xs text-[#5c5549]">
                  <div>{tile.resonanceCount} cartographers resonated</div>
                  <div className="flex items-center gap-1"><Heart size={12} /> RESONATES</div>
                </div>
              </div>
            ))}
          </div>

          <CreatorLinks variant="footer" />
        </div>
      )}

      {/* TILE DETAIL + ACTIONS */}
      {view === 'tile-detail' && selectedTile && (
        <div className="max-w-2xl mx-auto px-5 pt-12 pb-20">
          <button onClick={() => { setSelectedTile(null); setView('atlas') }} className="flex items-center gap-2 text-sm mb-6"><ArrowLeft size={16} /> back to the atlas</button>

          <div className="receipt p-8 md:p-10 rounded-3xl text-[#111]">
            <div className="uppercase tracking-[3px] text-xs text-[#5c5549]">{MOTIF_LABEL[selectedTile.motif]}</div>
            <div className="map-serif text-2xl leading-tight mt-3">“{selectedTile.tileText}”</div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={() => resonate(selectedTile.id)} className="flex items-center gap-2 px-5 py-2 rounded border border-[var(--border)] text-sm"><Heart size={15} /> THIS RESONATES WITH ME ({selectedTile.resonanceCount})</button>
            <button onClick={() => traceTerrain(selectedTile)} className="flex items-center gap-2 px-5 py-2 rounded bg-[#f4f1e9] text-[#111] text-sm">TRACE A SIMILAR TERRAIN IN MY OWN MAP</button>
            <button onClick={() => { navigator.clipboard.writeText(selectedTile.tileText); toast('Copied') }} className="flex items-center gap-2 px-5 py-2 rounded border border-[var(--border)] text-sm"><Copy size={15} /> COPY TEXT</button>
          </div>

          <div className="mt-8 text-xs text-[var(--text-subtle)] max-w-prose">
            These tiles are written by people who overthink replies, for people who overthink replies. They contain no identifying details by design. If a tile ever feels wrong or exposing, its maker can unpublish it from their private library.
          </div>
          <CreatorLinks variant="compact" />
        </div>
      )}

      {/* MY CARTOGRAPHY */}
      {view === 'my' && (
        <div className="max-w-4xl mx-auto px-5 pt-10 pb-20">
          <div className="flex items-center justify-between mb-6">
            <div className="text-2xl map-serif">My Cartography</div>
            <button onClick={() => startNewMap()} className="text-sm px-4 py-1.5 border rounded flex items-center gap-2"><Plus size={15} /> NEW MAP</button>
          </div>

          {myMaps.length === 0 && <div className="text-sm text-[var(--text-subtle)]">No maps yet on this key, or load failed. Start one from the landing page.</div>}

          <div className="space-y-3">
            {myMaps.map(m => (
              <div key={m.id} className="card p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm">{m.anchor}</div>
                  <div className="text-xs text-[var(--text-subtle)]">{formatDate(m.updatedAt)} · {m.signature}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openMap(m.id)} className="text-sm px-4 py-1 border rounded">OPEN MAP</button>
                </div>
              </div>
            ))}
          </div>

          <CreatorLinks variant="full" />
        </div>
      )}

      {/* PRIVACY DRAWER */}
      <AnimatePresence>
        {showPrivacy && (
          <div className="fixed inset-0 z-[70] bg-black/70 flex" onClick={() => setShowPrivacy(false)}>
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', bounce: 0.02 }} onClick={e => e.stopPropagation()} className="ml-auto w-full max-w-md bg-[#161512] border-l border-[var(--border)] p-6 overflow-auto">
              <div className="flex justify-between mb-6">
                <div className="text-lg">Privacy &amp; Ethics</div>
                <button onClick={() => setShowPrivacy(false)}><X /></button>
              </div>

              <div className="prose prose-invert text-sm space-y-4 text-[var(--text-bright)]">
                <p><strong>The entire purpose of this instrument is to make the uncertainty and cost of delayed replies visible without embarrassing anyone.</strong></p>
                <p>Your full map (anchor, private notes, exact pentagon coordinates, field notes) is stored under a hash derived from a secret key that only exists in your browser (or the device you pasted the key on). The Worker never sees the raw key, only the derived hash.</p>
                <p>Public wisdom tiles are written by you after seeing a live preview. They are deliberately short, de-identified, and normalizing. They never contain the name of the other person or specific identifying details.</p>
                <p>R2 artifacts are written only when you check the explicit opt-in boxes and confirm a second time.</p>
                <p>You can delete any map at any time. You can unpublish any tile you created.</p>
                <p>There are no accounts. There is no leaderboard. There is no “fixing” of you.</p>
              </div>

              <div className="mt-8 text-xs text-[var(--text-subtle)]">
                Built by Abhay Pratap Singh. The links below are the only way this kind of work is sustained.
              </div>
              <CreatorLinks variant="full" />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* KEY MODAL (for multi-device / recovery) */}
      <AnimatePresence>
        {showKeyModal && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" onClick={() => setShowKeyModal(false)}>
            <div onClick={e => e.stopPropagation()} className="card max-w-md w-full p-6">
              <div className="font-medium mb-2">Your Cartographer Key</div>
              <div className="text-xs text-[var(--text-subtle)] mb-3">This secret lives only in this browser (or wherever you paste it). Anyone with the key can see and edit the maps created with it. Treat it like a password.</div>
              <div className="font-mono text-sm break-all bg-[#0f0e0c] p-3 rounded border border-[var(--border)] select-all">{ownerKey}</div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => { navigator.clipboard.writeText(ownerKey); toast('Key copied') }} className="flex-1 py-2 border rounded text-sm">COPY KEY</button>
                <button onClick={() => {
                  const k = prompt('Paste a cartographer key to switch to it (this will load maps created with that key)')
                  if (k && k.startsWith('ca_')) {
                    localStorage.setItem('cartographer.ownerKey.v2', k)
                    setOwnerKey(k)
                    setShowKeyModal(false)
                    toast('Key switched. Reload My Cartography to see maps on that key.')
                  }
                }} className="flex-1 py-2 border rounded text-sm">USE A DIFFERENT KEY</button>
              </div>
              <div className="text-[10px] mt-4 text-[var(--text-subtle)]">Save this key somewhere safe if you want to continue the same maps from another device or after clearing browser data.</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      <Toaster />
    </div>
  )
}

function Toaster() {
  // sonner is already imported at root via main
  return null
}
